import { useField, useForm } from 'react-final-form';
import { useCallback, useMemo } from 'react';

import styles from './PassportGroupConditionForm.module.scss';
import { PassportGroupConditionNameEditor } from './PassportGroupConditionNameEditor';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { PassportGroupPassportTypeEnum } from '../enums/PassportGroupPassportTypeEnum';
import { CommonGroupCondition } from './CommonGroupCondition';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import { InfoBlock } from '../../../CorePackage/MainModule/components/InfoBlock';
import { PassportGroupTypeEnum } from '../../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';
import { StatusGroupCondition } from './StatusGroupCondition';
import { AssignmentGroupCondition } from './AssignmentGroupCondition';
import { SubjectGroupCondition } from './SubjectGroupCondition';
import { ObjectGroupCondition } from './ObjectGroupCondition';
import { EventGroupCondition } from './EventGroupCondition';
import { EventSubjectGroupCondition } from './EventSubjectGroupCondition';

interface IPassportGroupConditionFormProps {
  name: string;
  isVisible: boolean;
  groupType: PassportGroupTypeEnum;
}

export const PassportGroupConditionForm = ({ name, isVisible, groupType }: IPassportGroupConditionFormProps) => {
  const form = useForm();
  const categoryFieldName = `${name}.category`;
  const category: IGroupConditionForm['category'] = useField(categoryFieldName, { subscription: { value: true } }).input
    .value;

  const checkboxSize = useMemo(() => (groupType === PassportGroupTypeEnum.CANCELLATION ? 'col-3' : 'col-4'), [
    groupType,
  ]);

  const handleCategoryChange = useCallback(() => {
    form.change(`${name}.commonConditions`);
    form.change(`${name}.assignmentConditions`);
    form.change(`${name}.subjectConditions`);
    form.change(`${name}.objectConditions`);
    form.change(`${name}.eventSubjectConditions`);
    form.change(`${name}.eventConditions`);
    form.change(`${name}.statusConditions`);
  }, [form, name]);

  return (
    <div className={styles.ContentForm} style={{ display: isVisible ? 'block' : 'none' }}>
      <PassportGroupConditionNameEditor selectedCondition={name} />
      {!category && (
        <InfoBlock marginBottom=".7rem">
          Выбирая фильтр в рамках одного условия, Вы обеспечиваете применение всех этих фильтров одновременно
        </InfoBlock>
      )}
      {category === PassportGroupPassportTypeEnum.ERKNM && (
        <InfoBlock marginBottom=".7rem">
          Выбирая фильтр в рамках этого условия, Вы обеспечиваете применение фильтров только для контрольных (надзорных)
          мероприятий с взаимодействием с контролируемым лицом
        </InfoBlock>
      )}
      {category === PassportGroupPassportTypeEnum.PM && (
        <InfoBlock marginBottom=".7rem">
          Выбирая фильтр в рамках этого условия, Вы обеспечиваете применение фильтров только для профилактических
          мероприятий
        </InfoBlock>
      )}
      {category === PassportGroupPassportTypeEnum.DECISION_PASSPORT && (
        <InfoBlock marginBottom=".7rem">
          Выбирая фильтр в рамках этого условия, Вы обеспечиваете применение фильтров только для принятых решений по
          результатам контрольного (надзорного) мероприятия без взаимодействия с контролируемым лицом, профилактических
          мероприятий и использования специальных режимов государственного контроля (надзора)
        </InfoBlock>
      )}
      {category === PassportGroupPassportTypeEnum.ERP && (
        <InfoBlock marginBottom=".7rem">
          Выбирая фильтр в рамках этого условия, Вы обеспечиваете применение фильтров только для проверок ЕРП
        </InfoBlock>
      )}
      <Row title="Категория мероприятий">
        <FieldBlock size={checkboxSize}>
          <CheckboxField
            name={categoryFieldName}
            label="Контрольные (надзорные) мероприятия"
            value={PassportGroupPassportTypeEnum.ERKNM}
            id={`${categoryFieldName}.${PassportGroupPassportTypeEnum.ERKNM}`}
            type="radio"
            theme="system"
            labelClassName={styles.RadioField}
            onChange={handleCategoryChange}
          />
        </FieldBlock>
        <FieldBlock size={checkboxSize}>
          <CheckboxField
            name={categoryFieldName}
            label="Профилактические мероприятия"
            value={PassportGroupPassportTypeEnum.PM}
            id={`${categoryFieldName}.${PassportGroupPassportTypeEnum.PM}`}
            type="radio"
            theme="system"
            labelClassName={styles.RadioField}
            onChange={handleCategoryChange}
          />
        </FieldBlock>
        <FieldBlock size={checkboxSize}>
          <CheckboxField
            name={categoryFieldName}
            label="Решения (КНМ, ПМ без взаимодействия, СР)"
            value={PassportGroupPassportTypeEnum.DECISION_PASSPORT}
            id={`${categoryFieldName}.${PassportGroupPassportTypeEnum.DECISION_PASSPORT}`}
            type="radio"
            theme="system"
            labelClassName={styles.RadioField}
            onChange={handleCategoryChange}
          />
        </FieldBlock>
        {groupType === PassportGroupTypeEnum.DELETION && (
          <FieldBlock size={checkboxSize}>
            <CheckboxField
              name={categoryFieldName}
              label="Проверки ЕРП"
              value={PassportGroupPassportTypeEnum.ERP}
              id={`${categoryFieldName}.${PassportGroupPassportTypeEnum.ERP}`}
              type="radio"
              theme="system"
              labelClassName={styles.RadioField}
              onChange={handleCategoryChange}
            />
          </FieldBlock>
        )}
      </Row>
      {category && (
        <>
          <CommonGroupCondition name={`${name}.commonConditions`} category={category} />
          <AssignmentGroupCondition name={`${name}.assignmentConditions`} category={category} />
          <SubjectGroupCondition name={`${name}.subjectConditions`} category={category} />
          <ObjectGroupCondition name={`${name}.objectConditions`} category={category} />
          <EventSubjectGroupCondition name={`${name}.eventSubjectConditions`} category={category} />
          <EventGroupCondition name={`${name}.eventConditions`} category={category} />
          <StatusGroupCondition name={`${name}.statusConditions`} category={category} groupType={groupType} />
        </>
      )}
    </div>
  );
};
